import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Typography, Card, Row, Col, Button, Input, Spinner } from 'glintly-ui';
import { toast } from 'react-toastify';
import {
  useUser,
  useUpdateUser,
  useSubmitBankDetails,
  useShopsByOwner,
  useUpdateShop,
  useCities,
} from '../../../api';
import { uploadMultipleFiles } from '../../../api/services/uploadService';
import { setUser } from '../../../redux/slice/authSlice';
import {
  User,
  MapPin,
  Clock,
  Tag,
  CreditCard,
  Image as ImageIcon,
  Bell,
  Scissors,
} from 'lucide-react';
import styles from './settings.module.css';
import DashboardContainer from '../../../components/dashboardContainer';

const DAYS = [
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' },
];

const DAY_KEY_TO_NUM = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
};

const defaultDay = { open: '09:00', close: '21:00', isOpen: true };

const defaultNotifications = {
  emailBookings: true,
  smsReminders: false,
  pushPromos: true,
  marketing: false,
};

function workingHoursToTimings(wh) {
  if (!wh || !Array.isArray(wh) || wh.length === 0) return null;
  const t = {};
  DAYS.forEach(({ key }) => {
    const dayNum = DAY_KEY_TO_NUM[key];
    const row = wh.find((w) => w.day === dayNum);
    if (row) {
      t[key] = {
        open: row.open || '09:00',
        close: row.close || '21:00',
        isOpen: row.isClosed !== true,
      };
    } else {
      t[key] = { ...defaultDay };
    }
  });
  t.sunday = t.sunday || { open: '10:00', close: '20:00', isOpen: true };
  return t;
}

function timingsToWorkingHours(timings) {
  return DAYS.map(({ key }) => {
    const dayNum = DAY_KEY_TO_NUM[key];
    const row = timings[key] || defaultDay;
    return {
      day: dayNum,
      open: row.open,
      close: row.close,
      isClosed: !row.isOpen,
    };
  });
}

const VendorSettings = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const user = useSelector((state) => state.auth.user);
  const token = useSelector((state) => state.auth.token);

  /** Which shop is being edited for location, hours, and portfolio */
  const [selectedShopId, setSelectedShopId] = useState('');
  const [uploading, setUploading] = useState(false);

  const [shopAddress, setShopAddress] = useState({
    fullAddress: '',
    landmark: '',
    city: '',
    postalCode: '',
    country: 'India',
    latitude: '',
    longitude: '',
  });

  const { data: profile, isLoading: profileLoading } = useUser(user?._id);
  const { data: shops = [], isLoading: shopsLoading } = useShopsByOwner(user?._id);
  const { data: cities = [] } = useCities();

  const selectedShop = shops.find((s) => String(s._id) === String(selectedShopId)) || null;

  const updateUserMutation = useUpdateUser();
  const submitBankMutation = useSubmitBankDetails();
  const updateShopMutation = useUpdateShop();

  const [personal, setPersonal] = useState({
    name: '',
    email: '',
    phone: '',
    bio: '',
  });

  const [timings, setTimings] = useState(() => {
    const t = {};
    DAYS.forEach(({ key }) => {
      t[key] = { ...defaultDay };
    });
    t.sunday = { open: '10:00', close: '20:00', isOpen: true };
    return t;
  });

  const [packages, setPackages] = useState([
    { id: 1, name: 'Basic cut', price: '', duration: '30' },
    { id: 2, name: 'Cut + style', price: '', duration: '45' },
  ]);

  const [notifications, setNotifications] = useState({
    ...defaultNotifications,
  });

  const [banking, setBanking] = useState({
    accountName: '',
    bankName: '',
    accountNumber: '',
    ifsc: '',
    upi: '',
  });

  useEffect(() => {
    if (!profile) return;
    const u = profile;
    setPersonal({
      name: u.name || '',
      email: u.email || '',
      phone: u.mobile || u.phone || '',
      bio: u.bio || '',
    });
    setNotifications({
      ...defaultNotifications,
      ...(u.notificationPrefs || {}),
    });
    if (u.vendorPackages?.length) {
      setPackages(
        u.vendorPackages.map((p, i) => ({
          id: `pkg-${i}`,
          name: p.name || '',
          price: p.price != null ? String(p.price) : '',
          duration: p.duration != null ? String(p.duration) : '',
        }))
      );
    }
    const bd = u.bankDetails?.[0];
    if (bd) {
      setBanking({
        accountName: bd.accountHolderName || '',
        bankName: bd.bankName || '',
        accountNumber: bd.accountNumber || '',
        ifsc: bd.ifsc || '',
        upi: bd.upi || '',
      });
    }
  }, [profile]);

  useEffect(() => {
    if (!shops.length) {
      setSelectedShopId('');
      return;
    }
    setSelectedShopId((prev) => {
      const stillThere = prev && shops.some((s) => String(s._id) === String(prev));
      if (stillThere) return prev;
      return shops[0]?._id ? String(shops[0]._id) : '';
    });
  }, [shops]);

  useEffect(() => {
    if (!selectedShopId || !shops.length) return;
    const shop = shops.find((s) => String(s._id) === String(selectedShopId));
    if (!shop) return;
    if (shop.workingHours?.length) {
      const mapped = workingHoursToTimings(shop.workingHours);
      if (mapped) setTimings(mapped);
    } else {
      const t = {};
      DAYS.forEach(({ key }) => {
        t[key] = { ...defaultDay };
      });
      t.sunday = { open: '10:00', close: '20:00', isOpen: true };
      setTimings(t);
    }
  }, [selectedShopId, shops]);

  useEffect(() => {
    if (!selectedShopId || !shops.length) return;
    const shop = shops.find((s) => String(s._id) === String(selectedShopId));
    if (!shop) return;
    const a = shop.address || {};
    setShopAddress({
      fullAddress: a.fullAddress || a.street || '',
      landmark: a.landmark || '',
      city:
        typeof a.city === 'object' && a.city?._id
          ? String(a.city._id)
          : a.city
            ? String(a.city)
            : '',
      postalCode: a.postalCode || '',
      country: a.country || 'India',
      latitude:
        a.latitude != null
          ? String(a.latitude)
          : a.location?.coordinates?.[1] != null
            ? String(a.location.coordinates[1])
            : '',
      longitude:
        a.longitude != null
          ? String(a.longitude)
          : a.location?.coordinates?.[0] != null
            ? String(a.location.coordinates[0])
            : '',
    });
  }, [selectedShopId, shops]);

  const syncAuthUser = (nextUser) => {
    if (!nextUser || !token) return;
    const safe = { ...nextUser };
    if (safe.password) delete safe.password;
    dispatch(setUser({ user: safe, token }));
    localStorage.setItem('user', JSON.stringify(safe));
  };

  const savePersonal = async () => {
    if (!user?._id) return;
    try {
      const res = await updateUserMutation.mutateAsync({
        userId: user._id,
        userData: {
          name: personal.name.trim(),
          email: personal.email.trim(),
          mobile: personal.phone.trim(),
          bio: personal.bio.trim() || undefined,
        },
      });
      if (res?.user) syncAuthUser(res.user);
      toast.success('Personal information saved.');
      queryClient.invalidateQueries({
        queryKey: ['users', 'detail', user._id],
      });
    } catch (e) {
      toast.error(e.response?.data?.error || e.message || 'Save failed');
    }
  };

  const saveShopLocation = async () => {
    if (!selectedShop?._id) {
      toast.error('Select a shop first.');
      return;
    }
    const shop = selectedShop;
    const latStr = String(shopAddress.latitude).trim();
    const lngStr = String(shopAddress.longitude).trim();
    const address = {
      fullAddress: shopAddress.fullAddress.trim(),
      landmark: shopAddress.landmark.trim(),
      city: shopAddress.city || undefined,
      postalCode: shopAddress.postalCode.trim(),
      country: shopAddress.country.trim() || 'India',
      ...(latStr !== '' && lngStr !== ''
        ? {
            latitude: Number(latStr),
            longitude: Number(lngStr),
          }
        : {}),
    };
    try {
      await updateShopMutation.mutateAsync({
        id: shop._id,
        data: { address },
      });
      queryClient.invalidateQueries({ queryKey: ['shops', 'owner', user._id] });
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || 'Save failed');
    }
  };

  const saveHours = async () => {
    if (!selectedShop?._id) {
      toast.error('Select a shop first.');
      return;
    }
    try {
      await updateShopMutation.mutateAsync({
        id: selectedShop._id,
        data: { workingHours: timingsToWorkingHours(timings) },
      });
      queryClient.invalidateQueries({ queryKey: ['shops', 'owner', user._id] });
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || 'Save failed');
    }
  };

  const savePackages = async () => {
    if (!user?._id) return;
    const vendorPackages = packages
      .filter((p) => p.name.trim())
      .map((p) => ({
        name: p.name.trim(),
        price: Number(p.price) || 0,
        duration: Number(p.duration) || 30,
      }));
    try {
      const res = await updateUserMutation.mutateAsync({
        userId: user._id,
        userData: { vendorPackages },
      });
      if (res?.user) syncAuthUser(res.user);
      toast.success('Pricing & packages saved.');
      queryClient.invalidateQueries({
        queryKey: ['users', 'detail', user._id],
      });
    } catch (e) {
      toast.error(e.response?.data?.error || e.message || 'Save failed');
    }
  };

  const saveNotifications = async () => {
    if (!user?._id) return;
    try {
      const res = await updateUserMutation.mutateAsync({
        userId: user._id,
        userData: { notificationPrefs: notifications },
      });
      if (res?.user) syncAuthUser(res.user);
      toast.success('Notification preferences saved.');
      queryClient.invalidateQueries({
        queryKey: ['users', 'detail', user._id],
      });
    } catch (e) {
      toast.error(e.response?.data?.error || e.message || 'Save failed');
    }
  };

  const saveBanking = async () => {
    if (!user?._id) return;
    try {
      const res = await submitBankMutation.mutateAsync({
        userId: user._id,
        bankData: {
          bankName: banking.bankName.trim(),
          accountNumber: banking.accountNumber.trim(),
          ifsc: banking.ifsc.trim(),
          accountHolderName: banking.accountName.trim(),
          ...(banking.upi?.trim() ? { upi: banking.upi.trim() } : {}),
        },
      });
      if (res?.user) syncAuthUser(res.user);
      toast.success('Payment details saved.');
      queryClient.invalidateQueries({
        queryKey: ['users', 'detail', user._id],
      });
    } catch (e) {
      toast.error(e.response?.data?.error || e.message || 'Save failed');
    }
  };

  const onPortfolioChange = async (e) => {
    const files = Array.from(e.target.files || []);
    const shop = selectedShop;
    if (!files.length || !shop?._id) {
      if (!shop?._id) toast.error('Select a shop first.');
      return;
    }
    try {
      setUploading(true);
      const body = await uploadMultipleFiles(files);
      const uploaded = body?.data;
      const urls = Array.isArray(uploaded) ? uploaded.map((f) => f.url).filter(Boolean) : [];
      if (!urls.length) {
        toast.error('Upload did not return image URLs.');
        return;
      }
      const existing = Array.isArray(shop.images) ? shop.images : [];
      await updateShopMutation.mutateAsync({
        id: shop._id,
        data: { images: [...existing, ...urls] },
      });
      queryClient.invalidateQueries({ queryKey: ['shops', 'owner', user._id] });
    } catch (err) {
      toast.error(err.message || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const toggleDay = (key) => {
    setTimings((prev) => ({
      ...prev,
      [key]: { ...prev[key], isOpen: !prev[key].isOpen },
    }));
  };

  const setDayTime = (key, field, value) => {
    setTimings((prev) => ({
      ...prev,
      [key]: { ...prev[key], [field]: value },
    }));
  };

  if (profileLoading || shopsLoading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 280,
        }}
      >
        <Spinner size="large" />
        <Typography variant="body1" style={{ marginLeft: 16 }}>
          Loading settings…
        </Typography>
      </div>
    );
  }

  return (
    <DashboardContainer
      title="Profile &amp; settings"
      subtitle="Manage your salon profile, availability, pricing, and payouts in one place."
    >
      {shops.length > 0 && (
        <Card
          className={`${styles.section} ${styles.shopPickerCard}`}
          style={{ padding: '16px 20px' }}
        >
          <div className={styles.shopPickerRow}>
            <div className={styles.shopPickerField}>
              <span className={styles.label} style={{ marginBottom: 6 }}>
                Shop you&apos;re editing
              </span>
              <Input.Root name="settings-selected-shop">
                <Input.Control
                  type="select"
                  value={selectedShopId}
                  onChange={(e) => setSelectedShopId(e.target.value)}
                  options={shops.map((s) => ({
                    label: s.name || `Shop ${s._id}`,
                    value: String(s._id),
                  }))}
                />
              </Input.Root>
            </div>
            {selectedShop && (
              <Typography variant="body2" style={{ color: '#64748b', flex: '1 1 200px' }}>
                Location, hours, and portfolio below apply to <strong>{selectedShop.name}</strong>{' '}
                only. Switch shops to manage another address.
              </Typography>
            )}
          </div>
        </Card>
      )}

      {/* Personal information */}
      <Card className={styles.section} style={{ padding: '24px' }}>
        <div className={styles.sectionHeader}>
          <div>
            <div className={styles.sectionTitle}>
              <User size={18} style={{ color: '#dc2626' }} />
              <Typography variant="h6" style={{ color: '#1e293b', margin: 0 }}>
                Update personal information
              </Typography>
            </div>
            <p className={styles.subtitle}>
              Name, contact, and bio shown to customers on your salon profile.
            </p>
          </div>
          <Button
            variant="primary"
            size="sm"
            onClick={savePersonal}
            disabled={updateUserMutation.isPending}
          >
            Save
          </Button>
        </div>
        <div className={`${styles.formGrid} ${styles.twoCol}`}>
          <div className={styles.field}>
            <span className={styles.label}>Full name</span>
            <Input.Root name="personal-name">
              <Input.Control
                value={personal.name}
                onChange={(e) => setPersonal({ ...personal, name: e.target.value })}
                placeholder="Your name"
              />
            </Input.Root>
          </div>
          <div className={styles.field}>
            <span className={styles.label}>Email</span>
            <Input.Root name="personal-email">
              <Input.Control
                type="email"
                value={personal.email}
                onChange={(e) => setPersonal({ ...personal, email: e.target.value })}
                placeholder="you@example.com"
              />
            </Input.Root>
          </div>
          <div className={styles.field}>
            <span className={styles.label}>Phone</span>
            <Input.Root name="personal-phone">
              <Input.Control
                type="tel"
                value={personal.phone}
                onChange={(e) => setPersonal({ ...personal, phone: e.target.value })}
                placeholder="+91 …"
              />
            </Input.Root>
          </div>
        </div>
        <div className={styles.field} style={{ marginTop: 16 }}>
          <span className={styles.label}>Bio</span>
          <textarea
            className={styles.textarea}
            value={personal.bio}
            onChange={(e) => setPersonal({ ...personal, bio: e.target.value })}
            placeholder="Short description of you or your salon…"
          />
        </div>
      </Card>

      {/* Service offerings */}
      <Card className={styles.section} style={{ padding: '24px' }}>
        <div className={styles.sectionHeader}>
          <div>
            <div className={styles.sectionTitle}>
              <Scissors size={18} style={{ color: '#dc2626' }} />
              <Typography variant="h6" style={{ color: '#1e293b', margin: 0 }}>
                Manage service offerings
              </Typography>
            </div>
            <p className={styles.subtitle}>
              Add services, descriptions, and photos tied to your shops.
            </p>
          </div>
          <Button variant="primary" size="sm" onClick={() => navigate('/shops')}>
            Open services
          </Button>
        </div>
        <Typography variant="body2" style={{ color: '#64748b' }}>
          Use the services page to create and edit catalog items per shop. You can return here
          anytime.
        </Typography>
      </Card>

      {/* Shop location */}
      <Card className={styles.section} style={{ padding: '24px' }}>
        <div className={styles.sectionHeader}>
          <div>
            <div className={styles.sectionTitle}>
              <MapPin size={18} style={{ color: '#dc2626' }} />
              <Typography variant="h6" style={{ color: '#1e293b', margin: 0 }}>
                Shop location &amp; address
              </Typography>
            </div>
            <p className={styles.subtitle}>
              Updates this shop&apos;s address and map pin. If you change location, an admin must
              verify this shop again before it appears on the public map and salon list.
            </p>
            {selectedShop && (
              <Typography
                variant="body2"
                style={{
                  marginTop: 8,
                  color: selectedShop.verified ? '#059669' : '#d97706',
                }}
              >
                {selectedShop.verified
                  ? 'Status: verified — visible to customers'
                  : 'Status: pending verification — not shown publicly until an admin approves'}
              </Typography>
            )}
          </div>
          <Button
            variant="primary"
            size="sm"
            onClick={saveShopLocation}
            disabled={updateShopMutation.isPending || !selectedShop?._id}
          >
            Save location
          </Button>
        </div>
        <Row spacing={3}>
          <Col xs={12}>
            <div className={styles.field}>
              <span className={styles.label}>Street / full address</span>
              <Input.Root name="shop-full-address">
                <Input.Control
                  value={shopAddress.fullAddress}
                  onChange={(e) =>
                    setShopAddress({
                      ...shopAddress,
                      fullAddress: e.target.value,
                    })
                  }
                  placeholder="Building, street, area"
                />
              </Input.Root>
            </div>
          </Col>
          <Col xs={12} md={6}>
            <div className={styles.field}>
              <span className={styles.label}>City</span>
              <Input.Root name="shop-city">
                <Input.Control
                  type="select"
                  value={shopAddress.city}
                  onChange={(e) => setShopAddress({ ...shopAddress, city: e.target.value })}
                  options={[
                    { label: 'Select city', value: '' },
                    ...cities.map((c) => ({ label: c.cityName, value: c._id })),
                  ]}
                />
              </Input.Root>
            </div>
          </Col>
          <Col xs={12} md={6}>
            <div className={styles.field}>
              <span className={styles.label}>Landmark (optional)</span>
              <Input.Root name="shop-landmark">
                <Input.Control
                  value={shopAddress.landmark}
                  onChange={(e) => setShopAddress({ ...shopAddress, landmark: e.target.value })}
                  placeholder="Near …"
                />
              </Input.Root>
            </div>
          </Col>
          <Col xs={12} md={4}>
            <div className={styles.field}>
              <span className={styles.label}>Postal code</span>
              <Input.Root name="shop-postal">
                <Input.Control
                  value={shopAddress.postalCode}
                  onChange={(e) =>
                    setShopAddress({
                      ...shopAddress,
                      postalCode: e.target.value,
                    })
                  }
                  placeholder="PIN"
                />
              </Input.Root>
            </div>
          </Col>
          <Col xs={12} md={4}>
            <div className={styles.field}>
              <span className={styles.label}>Country</span>
              <Input.Root name="shop-country">
                <Input.Control
                  value={shopAddress.country}
                  onChange={(e) => setShopAddress({ ...shopAddress, country: e.target.value })}
                />
              </Input.Root>
            </div>
          </Col>
          <Col xs={12} md={4}>
            <div className={styles.field}>
              <span className={styles.label}>Latitude</span>
              <Input.Root name="shop-lat">
                <Input.Control
                  type="number"
                  step="any"
                  value={shopAddress.latitude}
                  onChange={(e) => setShopAddress({ ...shopAddress, latitude: e.target.value })}
                  placeholder="e.g. 19.076"
                />
              </Input.Root>
            </div>
          </Col>
          <Col xs={12} md={4}>
            <div className={styles.field}>
              <span className={styles.label}>Longitude</span>
              <Input.Root name="shop-lng">
                <Input.Control
                  type="number"
                  step="any"
                  value={shopAddress.longitude}
                  onChange={(e) =>
                    setShopAddress({
                      ...shopAddress,
                      longitude: e.target.value,
                    })
                  }
                  placeholder="e.g. 72.8777"
                />
              </Input.Root>
            </div>
          </Col>
        </Row>
      </Card>

      {/* Working hours */}
      <Card className={styles.section} style={{ padding: '24px' }}>
        <div className={styles.sectionHeader}>
          <div>
            <div className={styles.sectionTitle}>
              <Clock size={18} style={{ color: '#dc2626' }} />
              <Typography variant="h6" style={{ color: '#1e293b', margin: 0 }}>
                Working hours &amp; availability
              </Typography>
            </div>
            <p className={styles.subtitle}>Saved to the selected shop&apos;s working hours.</p>
          </div>
          <Button
            variant="primary"
            size="sm"
            onClick={saveHours}
            disabled={updateShopMutation.isPending || !selectedShop?._id}
          >
            Save hours
          </Button>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table className={styles.hoursTable}>
            <thead>
              <tr>
                <th>Day</th>
                <th>Open</th>
                <th>Hours</th>
              </tr>
            </thead>
            <tbody>
              {DAYS.map(({ key, label }) => (
                <tr key={key} className={!timings[key]?.isOpen ? styles.dayRowClosed : undefined}>
                  <td>
                    <label className={styles.notifyItem} style={{ margin: 0 }}>
                      <input
                        type="checkbox"
                        checked={timings[key]?.isOpen}
                        onChange={() => toggleDay(key)}
                      />
                      <span>{label}</span>
                    </label>
                  </td>
                  <td>{timings[key]?.isOpen ? 'Yes' : 'Closed'}</td>
                  <td>
                    {timings[key]?.isOpen ? (
                      <div className={styles.timeInputs}>
                        <Input.Root name={`hours-${key}-open`}>
                          <Input.Control
                            type="time"
                            value={timings[key]?.open || '09:00'}
                            onChange={(e) => setDayTime(key, 'open', e.target.value)}
                            style={{ maxWidth: 120 }}
                          />
                        </Input.Root>
                        <span className={styles.timeSep}>to</span>
                        <Input.Root name={`hours-${key}-close`}>
                          <Input.Control
                            type="time"
                            value={timings[key]?.close || '21:00'}
                            onChange={(e) => setDayTime(key, 'close', e.target.value)}
                            style={{ maxWidth: 120 }}
                          />
                        </Input.Root>
                      </div>
                    ) : (
                      <Typography variant="body2" style={{ color: '#94a3b8' }}>
                        —
                      </Typography>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Pricing & packages */}
      <Card className={styles.section} style={{ padding: '24px' }}>
        <div className={styles.sectionHeader}>
          <div>
            <div className={styles.sectionTitle}>
              <Tag size={18} style={{ color: '#dc2626' }} />
              <Typography variant="h6" style={{ color: '#1e293b', margin: 0 }}>
                Pricing &amp; packages
              </Typography>
            </div>
            <p className={styles.subtitle}>
              Stored on your user profile (vendor packages). Full product pricing lives under
              services.
            </p>
          </div>
          <Button
            variant="primary"
            size="sm"
            onClick={savePackages}
            disabled={updateUserMutation.isPending}
          >
            Save packages
          </Button>
        </div>
        {packages.map((pkg, index) => (
          <div key={pkg.id} className={styles.packageRow}>
            <div className={styles.field}>
              <span className={styles.label}>Package name</span>
              <Input.Root name={`pkg-${pkg.id}-name`}>
                <Input.Control
                  value={pkg.name}
                  onChange={(e) => {
                    const next = [...packages];
                    next[index] = { ...next[index], name: e.target.value };
                    setPackages(next);
                  }}
                />
              </Input.Root>
            </div>
            <div className={styles.field}>
              <span className={styles.label}>Price (₹)</span>
              <Input.Root name={`pkg-${pkg.id}-price`}>
                <Input.Control
                  type="number"
                  min={0}
                  placeholder="0"
                  value={pkg.price}
                  onChange={(e) => {
                    const next = [...packages];
                    next[index] = { ...next[index], price: e.target.value };
                    setPackages(next);
                  }}
                />
              </Input.Root>
            </div>
            <div className={styles.field}>
              <span className={styles.label}>Mins</span>
              <Input.Root name={`pkg-${pkg.id}-duration`}>
                <Input.Control
                  type="number"
                  min={5}
                  placeholder="30"
                  value={pkg.duration}
                  onChange={(e) => {
                    const next = [...packages];
                    next[index] = { ...next[index], duration: e.target.value };
                    setPackages(next);
                  }}
                />
              </Input.Root>
            </div>
          </div>
        ))}
        <Button
          variant="outline"
          size="sm"
          onClick={() =>
            setPackages((p) => [...p, { id: Date.now(), name: '', price: '', duration: '30' }])
          }
        >
          Add package row
        </Button>
      </Card>

      {/* Portfolio */}
      <Card className={styles.section} style={{ padding: '24px' }}>
        <div className={styles.sectionHeader}>
          <div>
            <div className={styles.sectionTitle}>
              <ImageIcon size={18} style={{ color: '#dc2626' }} />
              <Typography variant="h6" style={{ color: '#1e293b', margin: 0 }}>
                Portfolio images
              </Typography>
            </div>
            <p className={styles.subtitle}>
              Uploaded to the selected shop&apos;s image gallery (Cloudinary).
            </p>
          </div>
        </div>
        <div className={styles.uploadZone}>
          <Typography variant="body2" style={{ color: '#64748b' }}>
            Drag images here or choose files (JPG, PNG, WebP).
          </Typography>
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            className={styles.fileInput}
            onChange={onPortfolioChange}
            disabled={uploading || !selectedShop?._id}
          />
          <p className={styles.hint}>
            {uploading
              ? 'Uploading…'
              : selectedShop?._id
                ? 'Files are uploaded and stored on the selected shop.'
                : 'Select a shop above first.'}
          </p>
        </div>
      </Card>

      {/* Notifications */}
      <Card className={styles.section} style={{ padding: '24px' }}>
        <div className={styles.sectionHeader}>
          <div>
            <div className={styles.sectionTitle}>
              <Bell size={18} style={{ color: '#dc2626' }} />
              <Typography variant="h6" style={{ color: '#1e293b', margin: 0 }}>
                Notification preferences
              </Typography>
            </div>
            <p className={styles.subtitle}>Stored on your user profile.</p>
          </div>
          <Button
            variant="primary"
            size="sm"
            onClick={saveNotifications}
            disabled={updateUserMutation.isPending}
          >
            Save
          </Button>
        </div>
        <div className={styles.notifyList}>
          <label className={styles.notifyItem}>
            <input
              type="checkbox"
              checked={notifications.emailBookings}
              onChange={(e) =>
                setNotifications({
                  ...notifications,
                  emailBookings: e.target.checked,
                })
              }
            />
            <span>Email me for new bookings &amp; cancellations</span>
          </label>
          <label className={styles.notifyItem}>
            <input
              type="checkbox"
              checked={notifications.smsReminders}
              onChange={(e) =>
                setNotifications({
                  ...notifications,
                  smsReminders: e.target.checked,
                })
              }
            />
            <span>SMS reminders before appointments</span>
          </label>
          <label className={styles.notifyItem}>
            <input
              type="checkbox"
              checked={notifications.pushPromos}
              onChange={(e) =>
                setNotifications({
                  ...notifications,
                  pushPromos: e.target.checked,
                })
              }
            />
            <span>Push notifications for same-day slots</span>
          </label>
          <label className={styles.notifyItem}>
            <input
              type="checkbox"
              checked={notifications.marketing}
              onChange={(e) =>
                setNotifications({
                  ...notifications,
                  marketing: e.target.checked,
                })
              }
            />
            <span>Product tips &amp; partner offers (marketing)</span>
          </label>
        </div>
      </Card>

      {/* Banking */}
      <Card className={styles.section} style={{ padding: '24px' }}>
        <div className={styles.sectionHeader}>
          <div>
            <div className={styles.sectionTitle}>
              <CreditCard size={18} style={{ color: '#dc2626' }} />
              <Typography variant="h6" style={{ color: '#1e293b', margin: 0 }}>
                Payment &amp; banking details
              </Typography>
            </div>
            <p className={styles.subtitle}>Saved via your account&apos;s bank-details API.</p>
          </div>
          <Button
            variant="primary"
            size="sm"
            onClick={saveBanking}
            disabled={submitBankMutation.isPending}
          >
            Save
          </Button>
        </div>
        <Row spacing={3}>
          <Col xs={12} md={6}>
            <div className={styles.field}>
              <span className={styles.label}>Account holder name</span>
              <Input.Root name="bank-account-name">
                <Input.Control
                  value={banking.accountName}
                  onChange={(e) => setBanking({ ...banking, accountName: e.target.value })}
                  placeholder="As per bank records"
                />
              </Input.Root>
            </div>
          </Col>
          <Col xs={12} md={6}>
            <div className={styles.field}>
              <span className={styles.label}>Bank name</span>
              <Input.Root name="bank-bank-name">
                <Input.Control
                  value={banking.bankName}
                  onChange={(e) => setBanking({ ...banking, bankName: e.target.value })}
                />
              </Input.Root>
            </div>
          </Col>
          <Col xs={12} md={6}>
            <div className={styles.field}>
              <span className={styles.label}>Account number</span>
              <Input.Root name="bank-account-number">
                <Input.Control
                  type="password"
                  autoComplete="off"
                  value={banking.accountNumber}
                  onChange={(e) => setBanking({ ...banking, accountNumber: e.target.value })}
                  placeholder="••••••••"
                />
              </Input.Root>
            </div>
          </Col>
          <Col xs={12} md={6}>
            <div className={styles.field}>
              <span className={styles.label}>IFSC</span>
              <Input.Root name="bank-ifsc">
                <Input.Control
                  value={banking.ifsc}
                  onChange={(e) =>
                    setBanking({
                      ...banking,
                      ifsc: e.target.value.toUpperCase(),
                    })
                  }
                  placeholder="e.g. HDFC0001234"
                />
              </Input.Root>
            </div>
          </Col>
          <Col xs={12} md={6}>
            <div className={styles.field}>
              <span className={styles.label}>UPI ID (optional)</span>
              <Input.Root name="bank-upi">
                <Input.Control
                  value={banking.upi}
                  onChange={(e) => setBanking({ ...banking, upi: e.target.value })}
                  placeholder="name@upi"
                />
              </Input.Root>
            </div>
          </Col>
        </Row>
      </Card>
    </DashboardContainer>
  );
};

export default VendorSettings;
